import { InspectDescriptions } from "@/model/InspectGensha";
import { InspectRecommendCategory } from "@/model/InspectRecommend";
import {
  PartSelectionCircleDeleteApiActions,
  PartSelectionConfirmRegisterActions,
  PartSelectionConfirmSaveState,
  PartSelectionDeleteBuhinCallbackState,
  PartSelectionEditDialogState,
  PartSelectionInspectButtonChangeState,
  PartSelectionInitialDataState,
  PartSelectionInitRefActions,
  PartSelectionPageService as PartSelectionPageServiceInterface,
  PartSelectionRegisterActions,
  PartSelectionSaveLoadingActions,
  PartSelectionSavePartSelectionErrorActions,
  PartSelectionSavePartSelectionSuccessActions,
  PartSelectionSaveRequestState,
  PartSelectionSaveSuccessActions,
  PartSelectionSelectState,
  PartSelectionTemporarySaveActions,
  PartSelectionWorkBuhinRefAction
} from "@/model/PartSelectionPageService";
import { InspectButtonValue } from "@/model/InspectButtonValue";
import { INSPECT_TYPE } from "@/model/InspectType";
import { STATUS_SELECT_WORK_BUHIN } from "@/model/StatusSelectWorkBuhin";
import {
  applyPartSelectionCircleDeleteState,
  buildPartSelectionCircleDeleteApiActions,
  buildPartSelectionConfirmRegisterActions,
  buildPartSelectionConfirmSaveState,
  buildPartSelectionDeleteBuhinCallbackState,
  buildPartSelectionEditDialogState,
  buildPartSelectionInitRefActions,
  buildPartSelectionInspectButtonChangeState,
  buildPartSelectionInspectSaveItem,
  buildPartSelectionInspectSelectState,
  buildPartSelectionRecommendSelectState,
  buildPartSelectionRegisterActions,
  buildPartSelectionSaveLoadingActions,
  buildPartSelectionSavePartSelectionErrorActions,
  buildPartSelectionSavePartSelectionSuccessActions,
  buildPartSelectionSaveRequestState,
  buildPartSelectionSaveSuccessActions,
  buildPartSelectionShowMsgConfirmRefAction,
  buildPartSelectionTemporarySaveActions,
  getPartSelectionRecommendLength,
  isPartSelectionInspectSuccess,
  isPartSelectionNoSeibi,
  isPartSelectionRecommendSuccess,
  normalizePartSelectionInspectParamForSave,
  normalizePartSelectionSaveEditItem,
  shouldPartSelectionSendManualSeibiDeleteSocket,
  shouldShowPartSelectionTitle
} from "@/components/work-buhin/WorkBuhinPartSelection";
import requestService from "@/service/requestService";

export class PartSelectionPageService implements PartSelectionPageServiceInterface {
  private reqService: requestService;

  constructor() {
    this.reqService = new requestService();
  }

  getPartSelection(uketsukeExtendsId: number): Promise<any> {
    return this.reqService.getPartSelection(uketsukeExtendsId);
  }

  savePartSelection(data: any): Promise<any> {
    return this.reqService.savePartSelection(data);
  }

  saveByRequestState(saveRequestState: PartSelectionSaveRequestState): Promise<any> | null {
    if (saveRequestState.requestKind == "inspectLine") {
      return this.reqService.saveInspectLineSeibi(saveRequestState.data);
    }
    if (saveRequestState.requestKind == "recommend") {
      return this.reqService.saveInspectRecommend(saveRequestState.data);
    }
    if (saveRequestState.requestKind == "inspect") {
      return this.reqService.saveInspect(
        saveRequestState.data,
        saveRequestState.path
      );
    }
    return null;
  }

  sendSocket(uketsukeExtendsId: number, inspectId: number): Promise<any> {
    return this.reqService.sendSocket(uketsukeExtendsId, inspectId);
  }

  deleteManualSeibi(data: {
    sknUketsukeManualSeibiId: number;
    uketsukeExtendsId: number;
  }): Promise<any> {
    return this.reqService.deleteManualSeibi(data);
  }

  deleteManualBuhin(id: number): Promise<any> {
    return this.reqService.deleteManualBuhin(id);
  }

  saveCancel(data: any): Promise<any> {
    return this.reqService.saveCancel(data);
  }

  /*
   * WHY: partSelection の初期表示は inspectList/recommend の有無に依存するため、
   * API 応答の null 安全な読み取りを page から切り離す。
   */
  buildInitialDataState(res: any): PartSelectionInitialDataState {
    return {
      hasInspectList: !!(res && res.inspectList),
      inspectList: res && res.inspectList ? res.inspectList as InspectDescriptions[] : [],
      listRecommend: res && res.recommend ? res.recommend as InspectRecommendCategory[] : []
    };
  }

  /*
   * WHY: 初期表示時の WorkAndBuhin ref 操作は選択状態と不可分なので、
   * page が表示順や初期ステータス値を直接判断しないよう service に集約する。
   */
  buildInitRefActions(params: {
    first: boolean;
    activeInspectId: number;
    isInspect: boolean;
    listInspectByResult: InspectDescriptions[][];
    inspectList: InspectDescriptions[];
    listRecommend: InspectRecommendCategory[];
  }): PartSelectionInitRefActions {
    return buildPartSelectionInitRefActions({
      ...params,
      initialBuhinStatus: STATUS_SELECT_WORK_BUHIN.Initial,
      circleValue: InspectButtonValue.CIRCLE
    });
  }

  /*
   * WHY: 通常点検保存では画面表示用 field を取り除く必要があるため、
   * payload 正規化を page の保存制御から分離する。
   */
  buildInspectSaveItem(params: {
    inspectItem: InspectDescriptions;
    changeList: any;
  }): any {
    return buildPartSelectionInspectSaveItem(params);
  }

  /*
   * WHY: WorkAndBuhin の確定データを通常点検/おすすめに振り分ける判定は
   * sknInspectType の業務値に依存するため、画面から隠す。
   */
  buildConfirmSaveState(data: any): PartSelectionConfirmSaveState {
    return buildPartSelectionConfirmSaveState({
      data,
      recommendType: INSPECT_TYPE.RECOMMEND,
      recommendManualType: INSPECT_TYPE.RECOMMEND_MANUAL
    });
  }

  /*
   * WHY: 保存先 API、path、socket flag は inspect type と選択状態で決まるため、
   * page には保存要求 state だけを扱わせる。
   */
  buildSaveRequestState(params: {
    editItem: any;
    uketsukeExtendsId: number;
    changeList: any;
  }): PartSelectionSaveRequestState {
    return buildPartSelectionSaveRequestState({
      ...params,
      circleValue: InspectButtonValue.CIRCLE,
      doneStatus: STATUS_SELECT_WORK_BUHIN.Done,
      initialStatus: STATUS_SELECT_WORK_BUHIN.Initial,
      noNeedStatus: STATUS_SELECT_WORK_BUHIN.NoNeed,
      deleteStatus: STATUS_SELECT_WORK_BUHIN.delete,
      genshaType: INSPECT_TYPE.GENSHA,
      legal56Type: INSPECT_TYPE.LEGAL56,
      inspectLineType: INSPECT_TYPE.INSPECT_LINE,
      recommendType: INSPECT_TYPE.RECOMMEND,
      recommendManualType: INSPECT_TYPE.RECOMMEND_MANUAL
    });
  }

  /*
   * WHY: 通常点検クリック時の WorkAndBuhin 選択対象と ref action を service で決め、
   * page から seibiList/manualSeibiList の優先順を隠す。
   */
  buildInspectSelectState(inspect: InspectDescriptions): PartSelectionSelectState {
    return buildPartSelectionInspectSelectState({
      inspect,
      initialBuhinStatus: STATUS_SELECT_WORK_BUHIN.Initial
    });
  }

  /*
   * WHY: おすすめカテゴリは WorkAndBuhin へ渡す data shape が通常点検と違うため、
   * page ではなく service が activeInspect の形を作る。
   */
  buildRecommendSelectState(recCate: InspectRecommendCategory): PartSelectionSelectState {
    return buildPartSelectionRecommendSelectState(recCate);
  }

  /*
   * WHY: 登録時の未完了確認 dialog と emit 条件は画面遷移仕様なので、
   * page には action の実行だけを残す。
   */
  buildRegisterActions(params: {
    pageName: any;
    status: any;
    registerNotCompleteStatus: any;
  }): PartSelectionRegisterActions {
    return buildPartSelectionRegisterActions(params);
  }

  /*
   * WHY: 一時保存時に必要な emit/dialog 操作を action 化し、イベント制御を読みやすくする。
   */
  buildTemporarySaveActions(): PartSelectionTemporarySaveActions {
    return buildPartSelectionTemporarySaveActions();
  }

  /*
   * WHY: savePartSelection 成功後の reload と完了メッセージ条件を
   * API レスポンス判定として service に閉じ込める。
   */
  buildSavePartSelectionSuccessActions(params: {
    status: number;
    successStatus: number;
    body: any;
  }): PartSelectionSavePartSelectionSuccessActions {
    return buildPartSelectionSavePartSelectionSuccessActions(params);
  }

  /*
   * WHY: WorkAndBuhin の完了メッセージ表示は ref action の一種として扱い、
   * page が ref メソッド名を直接選ばないようにする。
   */
  buildShowMsgConfirmRefAction(): PartSelectionWorkBuhinRefAction {
    return buildPartSelectionShowMsgConfirmRefAction();
  }

  /*
   * WHY: 保存エラー通知で使う表示名の取り出しを service に寄せ、
   * page のエラー処理を通知実行だけにする。
   */
  buildSavePartSelectionErrorActions(params: {
    inspectItem: any;
  }): PartSelectionSavePartSelectionErrorActions {
    return buildPartSelectionSavePartSelectionErrorActions(params);
  }

  /*
   * WHY: 手入力整備削除後に socket 通知するかは ID の有無という業務境界なので、
   * callback から条件式を隠す。
   */
  shouldSendManualSeibiDeleteSocket(sknUketsukeInspectId: any): boolean {
    return shouldPartSelectionSendManualSeibiDeleteSocket(sknUketsukeInspectId);
  }

  /*
   * WHY: WorkAndBuhin からの部品削除 callback data は保存用 item へ整形が必要なため、
   * callback 処理を page に直書きしない。
   */
  buildDeleteBuhinCallbackState(data: any): PartSelectionDeleteBuhinCallbackState {
    return buildPartSelectionDeleteBuhinCallbackState(data);
  }

  /*
   * WHY: 通常点検の完了表示は workBuhinSelected の業務値に依存するため、
   * template から定数比較を隠す。
   */
  isInspectSuccess(inspect: InspectDescriptions): boolean {
    return isPartSelectionInspectSuccess({
      inspect,
      doneStatus: STATUS_SELECT_WORK_BUHIN.Done,
      noNeedStatus: STATUS_SELECT_WORK_BUHIN.NoNeed
    });
  }

  /*
   * WHY: おすすめカテゴリの完了判定は subItems 全体を見る必要があるため、
   * template にはカテゴリ単位の問い合わせだけを残す。
   */
  isRecommendSuccess(recommendCategory: InspectRecommendCategory): boolean {
    return isPartSelectionRecommendSuccess({
      recommendCategory,
      initialStatus: STATUS_SELECT_WORK_BUHIN.Initial
    });
  }

  /*
   * WHY: 見出し表示条件を template から分離し、listInspectByResult の形への依存を薄くする。
   */
  shouldShowTitle(items: any): boolean {
    return shouldShowPartSelectionTitle(items);
  }

  /*
   * WHY: 未完了登録の確認後 emit payload を service で作り、
   * page は dialog 結果に応じた action 実行だけにする。
   */
  buildConfirmRegisterActions(params: {
    pageName: any;
    registerNotCompleteStatus: any;
  }): PartSelectionConfirmRegisterActions {
    return buildPartSelectionConfirmRegisterActions(params);
  }

  /*
   * WHY: 編集 dialog の title/copy/hideTriangle は item 種別で分岐するため、
   * dialog 表示制御からデータ判定を切り離す。
   */
  buildEditDialogState(params: {
    item: any;
    isNoSeibi: boolean;
  }): PartSelectionEditDialogState {
    return buildPartSelectionEditDialogState({
      ...params,
      recommendType: INSPECT_TYPE.RECOMMEND,
      recommendManualType: INSPECT_TYPE.RECOMMEND_MANUAL
    });
  }

  /*
   * WHY: 判定ボタン変更時のおすすめ/通常点検の更新 field が異なるため、
   * page に inspect type 分岐を残さない。
   */
  buildInspectButtonChangeState(params: {
    item: any;
    result: number;
  }): PartSelectionInspectButtonChangeState {
    return buildPartSelectionInspectButtonChangeState({
      ...params,
      recommendType: INSPECT_TYPE.RECOMMEND,
      recommendManualType: INSPECT_TYPE.RECOMMEND_MANUAL,
      circleValue: InspectButtonValue.CIRCLE,
      noNeedStatus: STATUS_SELECT_WORK_BUHIN.NoNeed
    });
  }

  /*
   * WHY: 保存前に UI 専用 field を削除する処理を service に置き、
   * page は保存タイミングの制御に集中させる。
   */
  normalizeSaveEditItem(editItem: any): any {
    return normalizePartSelectionSaveEditItem(editItem);
  }

  /*
   * WHY: WorkAndBuhin 起点保存だけ loading 表示を変える仕様を action 化し、
   * 保存本体の分岐と混ざらないようにする。
   */
  buildSaveLoadingActions(params: {
    isWorkBuhin: boolean;
  }): PartSelectionSaveLoadingActions {
    return buildPartSelectionSaveLoadingActions(params);
  }

  /*
   * WHY: 保存成功後の socket/reload/完了メッセージ条件を service へ集約し、
   * API 成功判定を page に散らさない。
   */
  buildSaveSuccessActions(params: {
    status: number;
    successStatus: number;
    isSocket: number;
    sendSocket: boolean;
    isWorkBuhin: boolean;
  }): PartSelectionSaveSuccessActions {
    return buildPartSelectionSaveSuccessActions(params);
  }

  /*
   * WHY: おすすめカテゴリ数の算出条件を template から隠し、subItems の有無だけを公開する。
   */
  getRecommendLength(listRecommend: any): number {
    return getPartSelectionRecommendLength(listRecommend);
  }

  /*
   * WHY: 整備なし判定は固定文言と inspectService の結果を組み合わせるため、
   * page では入力情報だけを渡す形にする。
   */
  isNoSeibi(params: {
    inspect: InspectDescriptions;
    inspectServiceNoSeibi: boolean;
    fixedNoSeibiNames: string[];
  }): boolean {
    return isPartSelectionNoSeibi({
      ...params,
      noNeedStatus: STATUS_SELECT_WORK_BUHIN.NoNeed
    });
  }

  /*
   * WHY: CIRCLE 判定時に削除が必要な手入力整備/部品の抽出条件を service に閉じる。
   */
  buildCircleDeleteApiActions(inspectItem: InspectDescriptions): PartSelectionCircleDeleteApiActions {
    return buildPartSelectionCircleDeleteApiActions(inspectItem);
  }

  /*
   * WHY: CIRCLE 判定時の workBuhinSelected と部品削除 state の更新を service に集約し、
   * page には API 削除の実行だけを残す。
   */
  applyCircleDeleteState(inspectItem: InspectDescriptions): InspectDescriptions {
    return applyPartSelectionCircleDeleteState({
      inspectItem,
      noNeedStatus: STATUS_SELECT_WORK_BUHIN.NoNeed,
      initialStatus: STATUS_SELECT_WORK_BUHIN.Initial
    });
  }

  /*
   * WHY: 保存用 inspect param の最終正規化には複数の業務定数が必要なため、
   * page から定数注入をなくす。
   */
  normalizeInspectParamForSave(inspectItem: InspectDescriptions): InspectDescriptions {
    return normalizePartSelectionInspectParamForSave({
      inspectItem,
      initialStatus: STATUS_SELECT_WORK_BUHIN.Initial,
      doneStatus: STATUS_SELECT_WORK_BUHIN.Done,
      fakeDoneStatus: STATUS_SELECT_WORK_BUHIN.FakeDone,
      genshaType: INSPECT_TYPE.GENSHA,
      legal56Type: INSPECT_TYPE.LEGAL56
    });
  }
}
